import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Shield, Mail, CheckCircle, ArrowLeft, Lock, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// CART-01: only honor same-origin, path-relative redirects. Reject anything that
// could become an open redirect — absolute URLs, protocol-relative ("//evil"),
// or backslash-smuggled variants ("/\\evil") — falling back to a safe default.
function safeRedirect(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

export default function SignInPage() {
  const [tab, setTab] = useState('magic');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState(null);
  const { requestMagicLink, passwordLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get('redirect'));

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await requestMagicLink(email.trim(), redirectTo ? { redirect: redirectTo } : undefined);
      setSent(true);
      setCooldown(30);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send magic link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Countdown for the resend cooldown so we don't let customers (or the rate
  // limiter) get hammered with back-to-back link requests.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || loading || !email.trim()) return;
    setLoading(true);
    setError(null);
    setResendMsg(null);
    try {
      await requestMagicLink(email.trim());
      setCooldown(30);
      setResendMsg('A fresh link is on its way.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError(null);
    try {
      const user = await passwordLogin(email.trim(), password);
      const isStaff = user.account_type === 'staff' || user.role === 'admin';
      // CART-01: honor a same-origin ?redirect= (e.g. /order/checkout from a
      // guest cart) for customers. Staff always land in /team regardless of a
      // stray redirect param, so an admin sign-in never gets bounced into a
      // customer flow.
      const dest = isStaff ? '/team' : (redirectTo || '/account');
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (redirectTo || '/account');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm px-4"
      >
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 no-underline mb-6">
            <Shield className="h-6 w-6 text-ink" strokeWidth={1.5} />
            <span className="font-display text-lg font-medium text-ink">The Fashion Detective</span>
          </Link>
        </div>

        <Card className="bg-white border-stone/50 shadow-[var(--shadow-card)]">
          <CardContent className="p-6">
            {!sent ? (
              <>
                <h1 className="font-display text-xl font-medium text-ink mb-1" data-testid="signin-title">Sign In</h1>
                <p className="text-xs text-muted-foreground mb-4">
                  Choose your preferred sign-in method.
                </p>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleSignIn}
                  className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98] mb-4"
                  data-testid="google-signin-btn"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 0 0 1 12c0 1.77.42 3.45 1.18 4.94l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative mb-4" aria-hidden="true">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-stone/50" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-2 text-[10px] uppercase tracking-wide text-muted-foreground">or</span>
                  </div>
                </div>

                <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(null); }} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4" data-testid="signin-method-tabs">
                    <TabsTrigger value="magic" className="text-xs gap-1.5" data-testid="tab-magic-link">
                      <Mail className="h-3.5 w-3.5" /> Magic Link
                    </TabsTrigger>
                    <TabsTrigger value="password" className="text-xs gap-1.5" data-testid="tab-password">
                      <Lock className="h-3.5 w-3.5" /> Password
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="magic">
                    <form onSubmit={handleMagicLink} className="space-y-4" data-testid="signin-form-magic">
                      <div>
                        <Label htmlFor="email-magic" className="text-xs">Email Address</Label>
                        <Input
                          id="email-magic"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                          className="mt-1"
                          data-testid="signin-email-input"
                        />
                      </div>
                      {error && (
                        <Alert variant="destructive">
                          <AlertDescription className="text-xs">{error}</AlertDescription>
                        </Alert>
                      )}
                      <Button
                        type="submit"
                        className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                        disabled={loading}
                        data-testid="signin-submit-btn"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        {loading ? 'Sending...' : 'Send Magic Link'}
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="password">
                    <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="signin-form-password">
                      <div>
                        <Label htmlFor="email-pw" className="text-xs">Email Address</Label>
                        <Input
                          id="email-pw"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          required
                          className="mt-1"
                          data-testid="signin-email-pw-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="password" className="text-xs">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your password"
                          required
                          className="mt-1"
                          data-testid="signin-password-input"
                        />
                      </div>
                      {error && (
                        <Alert variant="destructive">
                          <AlertDescription className="text-xs">{error}</AlertDescription>
                        </Alert>
                      )}
                      <Button
                        type="submit"
                        className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                        disabled={loading}
                        data-testid="signin-password-submit-btn"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        {loading ? 'Signing in...' : 'Sign In'}
                      </Button>
                      <div className="text-center">
                        <Link
                          to="/account/forgot-password"
                          className="text-xs text-muted-foreground hover:text-ink no-underline"
                          data-testid="forgot-password-link"
                        >
                          Forgot your password?
                        </Link>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>

                <p className="text-[10px] text-muted-foreground text-center mt-4 leading-relaxed">
                  We'll never share your email. By signing in, you agree to our Terms of Service.
                </p>
              </>
            ) : (
              <div className="text-center py-4" data-testid="magic-link-sent">
                <CheckCircle className="h-10 w-10 mx-auto mb-3" style={{ color: '#0E6B5D' }} />
                <h2 className="font-display text-lg font-medium text-ink mb-2">Check Your Email</h2>
                <p className="text-sm text-muted-foreground mb-2">
                  We sent a sign-in link to <strong>{email}</strong>. Click the link to access your account.
                </p>
                <p className="text-[11px] text-muted-foreground mb-4 leading-relaxed">
                  The link expires in 15 minutes and can only be used once. Don&apos;t see it? Check your spam folder, then resend below.
                </p>
                {error && (
                  <Alert variant="destructive" className="mb-3 text-left">
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}
                {resendMsg && (
                  <p className="text-xs mb-3" style={{ color: '#0E6B5D' }} data-testid="signin-resend-confirmation">
                    {resendMsg}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={handleResend}
                    disabled={loading || cooldown > 0}
                    className="w-full gap-2 rounded-[12px] h-11 active:scale-[0.98]"
                    data-testid="signin-resend-link-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    {cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend Link'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSent(false); setEmail(''); setError(null); setResendMsg(null); setCooldown(0); }}
                    data-testid="signin-different-email-btn"
                  >
                    Use a Different Email
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-4">
          <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground no-underline hover:text-ink">
            <ArrowLeft className="h-3 w-3" /> Back to site
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
